// People & access (0022): pure helpers for the administrator screen, unit-tested in
// tests/unit/account.test.ts. The server decides every change; these only shape the view.
import type { Tone } from '@/components/ui'
import {
  type AccountAdminView,
  type AdminPerson,
  type Capability,
  CAPABILITY_LABELS,
  GLOBAL_CAPABILITIES,
  type GrantRequest,
  type InvitationView,
  type ManageAccessGrantArgs,
  SITE_CAPABILITIES,
} from '@/domain/identity'

export type PersonState = 'active' | 'suspended' | 'disabled' | 'invited' | 'invitation_expired' | 'no_account'

export const STATE_LABEL: Record<PersonState, string> = {
  active: 'Active',
  suspended: 'Suspended',
  disabled: 'Disabled',
  invited: 'Invited',
  invitation_expired: 'Invitation expired',
  no_account: 'No account',
}

export const STATE_TONE: Record<PersonState, Tone> = {
  active: 'ok',
  suspended: 'warn',
  disabled: 'bad',
  invited: 'info',
  invitation_expired: 'warn',
  no_account: 'neutral',
}

/** The invitation that matters now: the newest that is neither accepted nor superseded. */
export function openInvitation(p: AdminPerson): InvitationView | null {
  return p.invitations.find((i) => i.status === 'pending' || i.status === 'expired') ?? null
}

export function personState(p: AdminPerson): PersonState {
  const status = p.account?.status
  if (status === 'active' || status === 'suspended') return status
  const inv = openInvitation(p)
  if (inv?.status === 'pending') return 'invited'
  if (inv?.status === 'expired' && !inv.closed_at) return 'invitation_expired'
  if (status === 'disabled' || status === 'pending') return 'disabled'
  return 'no_account'
}

export type PersonAction = 'invite' | 'resend' | 'expire' | 'suspend' | 'reactivate' | 'disable' | 'access'

/** What an administrator may start for a person (never for themselves). */
export function personActions(p: AdminPerson, invitationsEnabled: boolean): PersonAction[] {
  if (p.is_self) return []
  const out: PersonAction[] = []
  const state = personState(p)
  const inv = openInvitation(p)
  if (invitationsEnabled && (state === 'no_account' || state === 'disabled')) out.push('invite')
  if (invitationsEnabled && inv && !inv.closed_at && (inv.status === 'pending' || inv.status === 'expired')) out.push('resend')
  if (inv?.status === 'pending') out.push('expire')
  if (p.account?.status === 'active') out.push('access', 'suspend', 'disable')
  if (p.account?.status === 'suspended') out.push('access', 'reactivate', 'disable')
  return out
}

export type PeopleFilter = { text: string; state: PersonState | '' }
export const EMPTY_PEOPLE_FILTER: PeopleFilter = { text: '', state: '' }

export function filterPeople(people: readonly AdminPerson[], f: PeopleFilter): AdminPerson[] {
  const q = f.text.trim().toLowerCase()
  return people.filter((p) => {
    if (f.state && personState(p) !== f.state) return false
    if (!q) return true
    return [p.display_name, p.organisation, p.staff_reference, p.account?.email, openInvitation(p)?.email]
      .some((v) => (v ?? '').toLowerCase().includes(q))
  })
}

/** Access as a set of keys "capability|site" (site '' for global capabilities). */
export type AccessSet = Set<string>
export const accessKey = (capability: Capability, siteId: string | null | undefined): string => `${capability}|${siteId ?? ''}`

export function currentAccess(p: AdminPerson): AccessSet {
  return new Set(p.grants.map((g) => accessKey(g.capability, g.site_id)))
}

export function accessToGrants(set: AccessSet): GrantRequest[] {
  return [...set].sort().map((k) => {
    const [capability, site] = k.split('|') as [Capability, string]
    return site ? { capability, site_id: site } : { capability }
  })
}

/** The manage_access_grant commands that turn `from` into `to`, grants first. */
export function accessChanges(accountId: string, from: AccessSet, to: AccessSet, reason: string): ManageAccessGrantArgs[] {
  const make = (k: string, action: 'grant' | 'revoke'): ManageAccessGrantArgs => {
    const [capability, site] = k.split('|') as [Capability, string]
    return { action, account_id: accountId, capability, ...(site ? { site_id: site } : {}), reason }
  }
  return [
    ...[...to].filter((k) => !from.has(k)).sort().map((k) => make(k, 'grant')),
    ...[...from].filter((k) => !to.has(k)).sort().map((k) => make(k, 'revoke')),
  ]
}

/** Ticking apply or evidence also shows the site; unticking view removes the others there. */
export function toggleAccess(set: AccessSet, capability: Capability, siteId: string | null, on: boolean): AccessSet {
  const next = new Set(set)
  const k = accessKey(capability, siteId)
  if (on) {
    next.add(k)
    if (siteId && capability !== 'view_site') next.add(accessKey('view_site', siteId))
  } else {
    next.delete(k)
    if (siteId && capability === 'view_site') {
      next.delete(accessKey('apply_permits', siteId))
      next.delete(accessKey('record_evidence', siteId))
    }
  }
  return next
}

/** One line per site and the global capabilities, for a card. */
export function accessSummary(p: AdminPerson, sites: AccountAdminView['sites']): string[] {
  const lines: string[] = []
  for (const s of sites) {
    const caps = SITE_CAPABILITIES.filter((c) => p.grants.some((g) => g.capability === c && g.site_id === s.id))
    if (caps.length > 0) lines.push(`${s.name}: ${caps.map((c) => CAPABILITY_LABELS[c].label.toLowerCase()).join(', ')}`)
  }
  const global = GLOBAL_CAPABILITIES.filter((c) => p.grants.some((g) => g.capability === c && g.site_id === null))
  for (const c of global) lines.push(CAPABILITY_LABELS[c].label)
  return lines
}

/** The message the administrator sends the invited person (the system does not send it). */
export function invitationMessage(opts: { name: string; email: string; signInUrl: string; expiresAt: string; fmt: (iso: string) => string }): string {
  return [
    `${opts.name}, you are invited to BadrEOR Permit to Work.`,
    `Open ${opts.signInUrl}, enter ${opts.email} and the 6-digit code that is emailed to you.`,
    `The invitation expires on ${opts.fmt(opts.expiresAt)} (Oman time). Sign in only with your own email address; never share a code.`,
  ].join('\n')
}

export const INVITATION_DAYS = [1, 3, 7, 14, 30] as const

/** People table sort (People & access). */
export type PeopleSortKey = 'name' | 'email' | 'organisation' | 'state' | 'access'
export type PeopleSort = { key: PeopleSortKey; dir: 'asc' | 'desc' }
export const DEFAULT_PEOPLE_SORT: PeopleSort = { key: 'name', dir: 'asc' }
const STATE_ORDER: PersonState[] = ['active', 'invited', 'invitation_expired', 'no_account', 'suspended', 'disabled']

export function personEmail(p: AdminPerson): string | null {
  return p.account?.email ?? openInvitation(p)?.email ?? null
}

export function sortPeople(people: readonly AdminPerson[], s: PeopleSort, sites: AccountAdminView['sites']): AdminPerson[] {
  const text = (p: AdminPerson): string => {
    if (s.key === 'email') return personEmail(p) ?? ''
    if (s.key === 'organisation') return p.organisation ?? ''
    if (s.key === 'access') return accessSummary(p, sites).join(', ')
    return p.display_name
  }
  const sign = s.dir === 'asc' ? 1 : -1
  return [...people].sort((a, b) => {
    const c = s.key === 'state'
      ? STATE_ORDER.indexOf(personState(a)) - STATE_ORDER.indexOf(personState(b))
      : text(a).localeCompare(text(b), 'en', { sensitivity: 'base' })
    // Empty values last in both directions; ties by name.
    if (s.key !== 'state' && s.key !== 'name' && (text(a) === '') !== (text(b) === '')) return text(a) === '' ? 1 : -1
    return c !== 0 ? sign * c : a.display_name.localeCompare(b.display_name, 'en', { sensitivity: 'base' })
  })
}

/** Clicking a column header: the same column flips direction, a new one starts ascending. */
export function nextSort(current: PeopleSort, key: PeopleSortKey): PeopleSort {
  return current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
}
